// AccountPage — /account. Elevated: profile header + stats + tabbed sections.
// Travellers tab: real GET /travellers via useQuery (live BE).
// Other tabs: visual-only with mock data.
// TODO(account-crud-PR): Wire Edit profile, Add traveller, Payment CRUD when account BE lands.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Shimmer, EmptyState, notYetWiredToast } from '@/components/primitives';
import { StatsCard } from '@/components/StatsCard';
import { ProfileTabs, type AccountTab } from '@/components/ProfileTabs';
import { SiteFooter } from '@/components/SiteFooter';
import type { User } from '@flight/shared/types';

const F = '"Inter Variable","Inter",system-ui,sans-serif';
const DISPLAY = '"Fraunces Variable","Fraunces",Georgia,serif';

const T = {
  bg: '#0A0F1C', bg2: '#0F1628', surface: '#131C30', surface2: '#1A2540',
  ink: '#F4F3F0', inkDim: '#8C98B0', inkLow: '#4E5E78',
  primary: '#E8441A', accent: '#4B8EDB',
  line: 'rgba(255,255,255,0.07)', lineHi: 'rgba(255,255,255,0.14)',
};

// ─── API types ────────────────────────────────────────────────────────────────
interface TravellersResponse {
  data?: {
    travellers?: Array<{
      id: string;
      firstName: string;
      lastName: string;
      email?: string;
      dateOfBirth?: string;
      passportNumber?: string;
      nationality?: string;
    }>;
  };
}

interface MeResponse {
  data?: { user?: User };
}

// ─── Sub-sections ─────────────────────────────────────────────────────────────
function ProfileSection({ me, meLoading }: { me: User | null; meLoading: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{
        background: T.surface, borderRadius: 20, padding: '24px 28px',
        border: `1px solid ${T.line}`,
      }}>
        <div style={{ fontFamily: F, fontSize: 11, letterSpacing: 2, color: T.inkDim, fontWeight: 600, marginBottom: 20, textTransform: 'uppercase' }}>
          Personal info
        </div>
        {meLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[1, 2, 3].map(i => <Shimmer key={i} className="h-10 w-full rounded-xl"/>)}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            {[
              { label: 'Full name', value: me?.fullName ?? 'Not set' },
              { label: 'Email', value: me?.email ?? 'Not set' },
              { label: 'Phone', value: me?.phone ?? 'Not set' },
              { label: 'Member since', value: '2024' },
            ].map(field => (
              <div key={field.label} style={{
                padding: '14px 16px', background: T.bg2, borderRadius: 14,
                border: `1px solid ${T.line}`,
              }}>
                <div style={{ fontFamily: F, fontSize: 10, letterSpacing: 1.5, color: T.inkLow, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>
                  {field.label}
                </div>
                <div style={{ fontFamily: F, fontSize: 15, fontWeight: 600, color: T.ink }}>
                  {field.value}
                </div>
              </div>
            ))}
          </div>
        )}
        <button
          onClick={() => notYetWiredToast('Profile edit')}
          style={{
            marginTop: 16, height: 40, padding: '0 20px', borderRadius: 999,
            background: 'transparent', color: T.primary,
            border: `1px solid rgba(232,68,26,0.35)`,
            fontFamily: F, fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}
        >
          Edit profile
        </button>
      </div>
    </div>
  );
}

function TravellersSection({
  travellers,
  loading,
}: {
  travellers: Array<{ id: string; firstName: string; lastName: string; email?: string; passportNumber?: string; nationality?: string }>;
  loading: boolean;
}) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontFamily: F, fontSize: 11, letterSpacing: 2, color: T.inkDim, fontWeight: 600, textTransform: 'uppercase' }}>
          Saved travellers
        </div>
        <button
          onClick={() => notYetWiredToast('Traveller management')}
          style={{
            padding: '8px 16px', borderRadius: 999,
            background: 'transparent', color: T.primary,
            border: `1px solid rgba(232,68,26,0.3)`,
            fontFamily: F, fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}
        >+ Add traveller</button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[1, 2].map(i => <Shimmer key={i} className="h-16 w-full rounded-2xl"/>)}
        </div>
      ) : travellers.length === 0 ? (
        <EmptyState
          title="No travellers yet"
          body="Add yourself and travel companions to speed up booking."
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {travellers.map(t => (
            <div key={t.id} style={{
              display: 'flex', alignItems: 'center', gap: 16,
              padding: '16px 20px', background: T.surface,
              borderRadius: 18, border: `1px solid ${T.line}`,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: 'linear-gradient(135deg, #1B3A6B, #4B8EDB)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontFamily: F, fontSize: 16, fontWeight: 700, flexShrink: 0,
              }}>
                {t.firstName.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: F, fontSize: 15, fontWeight: 700, color: T.ink }}>
                  {t.firstName} {t.lastName}
                </div>
                {t.email && (
                  <div style={{ fontFamily: F, fontSize: 12, color: T.inkDim, marginTop: 2 }}>{t.email}</div>
                )}
                {t.nationality && (
                  <div style={{ fontFamily: F, fontSize: 11, color: T.inkLow, marginTop: 1 }}>{t.nationality}</div>
                )}
              </div>
              {t.passportNumber && (
                <div style={{
                  padding: '4px 10px', borderRadius: 999,
                  background: 'rgba(75,142,219,0.12)', color: '#4B8EDB',
                  fontFamily: F, fontSize: 11, fontWeight: 700,
                }}>Passport on file</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PreferencesSection() {
  const [emailAlerts, setEmailAlerts] = useState(true);
  const [pushAlerts, setPushAlerts] = useState(true);
  const [currency, setCurrency] = useState('INR (₹)');

  function Toggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
    return (
      <button
        onClick={onToggle}
        style={{
          width: 44, height: 24, borderRadius: 999, border: 'none', cursor: 'pointer',
          background: on ? T.primary : 'rgba(255,255,255,0.12)',
          position: 'relative', transition: 'background 0.2s', flexShrink: 0,
        }}
      >
        <div style={{
          position: 'absolute', top: 3, left: on ? 22 : 3,
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
        }}/>
      </button>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontFamily: F, fontSize: 11, letterSpacing: 2, color: T.inkDim, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>
        Notifications &amp; preferences
      </div>
      {[
        {
          label: 'Email price alerts',
          sub: 'Get notified when fares drop for saved routes',
          value: emailAlerts,
          toggle: () => setEmailAlerts(v => !v),
        },
        {
          label: 'Push notifications',
          sub: 'Flight updates, gate changes, boarding reminders',
          value: pushAlerts,
          toggle: () => setPushAlerts(v => !v),
        },
      ].map(pref => (
        <div key={pref.label} style={{
          display: 'flex', alignItems: 'center', gap: 16,
          padding: '16px 20px', background: T.surface,
          borderRadius: 18, border: `1px solid ${T.line}`,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: F, fontSize: 14, fontWeight: 600, color: T.ink }}>{pref.label}</div>
            <div style={{ fontFamily: F, fontSize: 12, color: T.inkDim, marginTop: 2 }}>{pref.sub}</div>
          </div>
          <Toggle on={pref.value} onToggle={pref.toggle}/>
        </div>
      ))}

      {/* Currency selector (mock) */}
      <div style={{
        padding: '16px 20px', background: T.surface,
        borderRadius: 18, border: `1px solid ${T.line}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontFamily: F, fontSize: 14, fontWeight: 600, color: T.ink }}>Display currency</div>
          <div style={{ fontFamily: F, fontSize: 12, color: T.inkDim, marginTop: 2 }}>
            Prices shown in selected currency
          </div>
        </div>
        <select
          value={currency}
          onChange={e => setCurrency(e.target.value)}
          style={{
            height: 36, padding: '0 12px', borderRadius: 12,
            background: T.bg2, color: T.ink,
            border: `1px solid ${T.lineHi}`,
            fontFamily: F, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          <option>INR (₹)</option>
          <option>USD ($)</option>
          <option>EUR (€)</option>
          <option>GBP (£)</option>
        </select>
      </div>
    </div>
  );
}

function PaymentSection() {
  const cards = [
    { last4: '4521', brand: 'Visa', label: 'Default · 3% cashback', color: '#1B3A6B' },
    { last4: '8290', brand: 'Mastercard', label: 'Personal', color: '#5C0931' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <div style={{ fontFamily: F, fontSize: 11, letterSpacing: 2, color: T.inkDim, fontWeight: 600, textTransform: 'uppercase' }}>
          Saved cards
        </div>
        <button
          onClick={() => notYetWiredToast('Payment methods')}
          style={{
            padding: '8px 16px', borderRadius: 999,
            background: 'transparent', color: T.primary,
            border: `1px solid rgba(232,68,26,0.3)`,
            fontFamily: F, fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}
        >+ Add card</button>
      </div>

      {cards.map(card => (
        <div key={card.last4} style={{
          display: 'flex', alignItems: 'center', gap: 16,
          padding: '16px 20px', background: T.surface,
          borderRadius: 18, border: `1px solid ${T.line}`,
        }}>
          <div style={{
            width: 44, height: 30, borderRadius: 8,
            background: card.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontFamily: F, fontSize: 11, fontWeight: 800,
          }}>{card.brand}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: F, fontSize: 14, fontWeight: 700, color: T.ink }}>
              •••• •••• •••• {card.last4}
            </div>
            <div style={{ fontFamily: F, fontSize: 12, color: T.inkDim, marginTop: 2 }}>{card.label}</div>
          </div>
          <button
            onClick={() => notYetWiredToast('Card management')}
            style={{
              padding: '6px 12px', borderRadius: 999,
              background: 'transparent', color: T.inkDim,
              border: `1px solid ${T.line}`,
              fontFamily: F, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            }}
          >Remove</button>
        </div>
      ))}

      <div style={{
        marginTop: 8, padding: '16px 20px', background: T.bg2,
        borderRadius: 18, border: `1px solid ${T.line}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{ fontSize: 20 }}>🔒</div>
        <div style={{ fontFamily: F, fontSize: 12, color: T.inkDim, lineHeight: 1.5 }}>
          Card details are tokenised via Razorpay and never stored on our servers. PCI-SAQ-A compliant.
        </div>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export function AccountPage() {
  const { status, user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AccountTab>('profile');

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const res = await api.get<MeResponse>('/users/me');
      return res.data.data?.user ?? null;
    },
    enabled: status === 'authed',
  });

  const { data: travellersData, isLoading: travLoading } = useQuery({
    queryKey: ['travellers'],
    queryFn: async () => {
      const res = await api.get<TravellersResponse>('/travellers');
      return res.data.data?.travellers ?? [];
    },
    enabled: status === 'authed',
  });

  if (status === 'anon') {
    return (
      <div style={{
        background: T.bg, minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: F, fontSize: 48, marginBottom: 12 }}>✈</div>
          <h2 style={{ fontFamily: DISPLAY, fontSize: 36, fontWeight: 500, color: T.ink, margin: '0 0 12px', letterSpacing: -0.02 }}>
            Sign in to continue.
          </h2>
          <p style={{ fontFamily: F, fontSize: 15, color: T.inkDim, margin: '0 0 24px' }}>
            Your account, trips, and settings live here.
          </p>
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '14px 32px', borderRadius: 999,
              background: T.primary, color: '#fff', border: 'none',
              fontFamily: F, fontSize: 15, fontWeight: 700, cursor: 'pointer',
            }}
          >Sign in →</button>
        </div>
      </div>
    );
  }

  const me = meData ?? user;
  const travellers = travellersData ?? [];
  const initials = (me?.fullName ?? me?.email ?? 'U')
    .split(' ')
    .slice(0, 2)
    .map(w => w.charAt(0).toUpperCase())
    .join('');

  return (
    <div style={{ background: T.bg, minHeight: '100vh' }}>
      {/* Profile hero header */}
      <div style={{
        background: `linear-gradient(180deg, #0F1628 0%, ${T.bg} 100%)`,
        padding: '48px clamp(16px, 4vw, 64px) 0',
        borderBottom: `1px solid ${T.line}`,
      }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          {/* Top row: avatar + name + actions */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, paddingBottom: 32 }}>
            {/* Avatar */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{
                width: 88, height: 88, borderRadius: '50%',
                background: 'linear-gradient(135deg, #E8441A 0%, #1B3A6B 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: DISPLAY, fontSize: 32, fontWeight: 500, color: '#fff',
                border: `3px solid ${T.bg2}`,
                boxShadow: '0 0 0 1px rgba(232,68,26,0.3)',
              }}>
                {meLoading ? '…' : initials}
              </div>
              {/* Verified dot */}
              <div style={{
                position: 'absolute', bottom: 4, right: 4,
                width: 20, height: 20, borderRadius: '50%',
                background: '#22C55E', border: `2px solid ${T.bg}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10,
              }}>✓</div>
            </div>

            {/* Name + meta */}
            <div style={{ flex: 1, paddingBottom: 4 }}>
              {meLoading ? (
                <>
                  <Shimmer className="h-8 w-48 mb-2"/>
                  <Shimmer className="h-4 w-64"/>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <h1 style={{
                      fontFamily: DISPLAY,
                      fontSize: 'clamp(28px,3.5vw,42px)',
                      fontWeight: 500,
                      color: T.ink,
                      margin: 0,
                      lineHeight: 1,
                      letterSpacing: -0.02,
                    }}>{me?.fullName ?? 'Traveller'}</h1>
                    <span style={{
                      padding: '3px 10px', borderRadius: 999,
                      background: 'rgba(34,197,94,0.15)', color: '#22C55E',
                      fontFamily: F, fontSize: 11, fontWeight: 700,
                    }}>✓ Verified</span>
                  </div>
                  <div style={{ fontFamily: F, fontSize: 14, color: T.inkDim, marginTop: 6 }}>
                    {me?.email}
                    {me?.phone && (
                      <span style={{ marginLeft: 12, color: T.inkLow }}>· {me.phone}</span>
                    )}
                  </div>
                  <div style={{ fontFamily: F, fontSize: 12, color: T.inkLow, marginTop: 4 }}>
                    Member since 2024
                  </div>
                </>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, paddingBottom: 4 }}>
              <button
                onClick={() => navigate('/trips')}
                style={{
                  height: 40, padding: '0 18px', borderRadius: 999,
                  background: 'transparent', color: T.ink,
                  border: `1px solid ${T.lineHi}`,
                  fontFamily: F, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >My trips</button>
              <button
                onClick={() => { void logout(); navigate('/'); }}
                style={{
                  height: 40, padding: '0 18px', borderRadius: 999,
                  background: 'rgba(232,68,26,0.08)', color: T.primary,
                  border: '1px solid rgba(232,68,26,0.2)',
                  fontFamily: F, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >Sign out</button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '40px clamp(16px, 4vw, 64px) 80px' }}>

        {/* Stats card */}
        <div style={{ marginBottom: 32 }}>
          <StatsCard stats={[
            { label: 'Trips taken',   value: String(travellers.length > 0 ? 7 : 0), icon: '✈', sub: '+2 upcoming' },
            { label: 'Miles earned',  value: '84,201',  icon: '⭐', sub: 'Frequent flyer' },
            { label: 'Total saved',   value: '₹12,400', icon: '💰', sub: 'vs. list price' },
          ]}/>
        </div>

        {/* Tabs */}
        <div style={{ marginBottom: 28 }}>
          <ProfileTabs active={activeTab} onChange={setActiveTab}/>
        </div>

        {/* Tab content */}
        {activeTab === 'profile' && (
          <ProfileSection me={me} meLoading={meLoading}/>
        )}
        {activeTab === 'travellers' && (
          <TravellersSection travellers={travellers} loading={travLoading}/>
        )}
        {activeTab === 'preferences' && (
          <PreferencesSection/>
        )}
        {activeTab === 'payment' && (
          <PaymentSection/>
        )}
      </div>

      <SiteFooter/>
    </div>
  );
}
